import { css } from "dx-styles";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { ApiError, api } from "../api/client";
import type { PublicMeta } from "../api/types";
import {
  CopyButton,
  button,
  card,
  errorText,
  heading,
  layout,
  mono,
  muted,
  row,
  stack,
  subheading,
} from "../components/primitives";
import { accessCodeFromFragment, relativeTime, shareLink, subscribeDeepLink } from "../lib/format";
import { tokens } from "../theme/tokens";

const counts = css({
  display: "flex",
  gap: tokens.space.xl,
  "& strong": { fontSize: "26px", display: "block" },
});

const field = css({
  minHeight: "36px",
  padding: `0 ${tokens.space.sm}`,
  borderRadius: tokens.radius.sm,
  border: `1px solid ${tokens.color.rule}`,
  backgroundColor: tokens.color.paper,
});

type State =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly meta: PublicMeta }
  | { readonly kind: "needsCode" }
  | { readonly kind: "gone" }
  | { readonly kind: "missing" }
  | { readonly kind: "failed"; readonly message: string };

/**
 * The public page for a published collection.
 *
 * The access code arrives in the URL fragment and stays there: never in a query
 * string, never in the document title, never in a `Referer`. It is only ever sent
 * as a bearer token on the meta request.
 */
export function Landing() {
  const { publicId = "" } = useParams();
  const [code, setCode] = useState<string | null>(() => accessCodeFromFragment(window.location.hash));
  const [typedCode, setTypedCode] = useState("");
  const [state, setState] = useState<State>({ kind: "loading" });

  const load = useCallback(
    async (accessCode: string | null) => {
      setState({ kind: "loading" });
      try {
        setState({ kind: "ready", meta: await api.publicMeta(publicId, accessCode) });
      } catch (caught) {
        if (caught instanceof ApiError) {
          if (caught.status === 401 || caught.status === 403) {
            setState({ kind: "needsCode" });
            return;
          }
          if (caught.status === 410) {
            setState({ kind: "gone" });
            return;
          }
          if (caught.status === 404) {
            setState({ kind: "missing" });
            return;
          }
        }
        setState({
          kind: "failed",
          message: caught instanceof Error ? caught.message : "Could not load this collection.",
        });
      }
    },
    [publicId],
  );

  useEffect(() => {
    void load(code);
  }, [code, load]);

  // A fragment-only change does not reload the document, so pasting a link with
  // `#code=` into the address bar of a page that is already open would otherwise
  // do nothing at all.
  useEffect(() => {
    const onHashChange = () => setCode(accessCodeFromFragment(window.location.hash));
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const bundleUrl = `${window.location.origin}/c/${publicId}.json`;

  return (
    <main className={layout}>
      {state.kind === "loading" ? (
        <p className={muted}>Loading…</p>
      ) : state.kind === "ready" ? (
        <article className={stack}>
          <p className={muted}>Shared collection</p>
          <h1 className={heading}>{state.meta.name}</h1>
          {state.meta.description ? <p>{state.meta.description}</p> : null}
          <div className={counts}>
            <span>
              <strong>{state.meta.templateCount}</strong>
              <span className={muted}>{state.meta.templateCount === 1 ? "template" : "templates"}</span>
            </span>
            <span>
              <strong>{state.meta.itemCount}</strong>
              <span className={muted}>{state.meta.itemCount === 1 ? "item" : "items"}</span>
            </span>
          </div>
          <p className={muted}>
            Updated {relativeTime(state.meta.updatedAt)} · revision {state.meta.revisionNumber}. Subscribing
            keeps these templates current on your phone; your own edits stay yours once you make a local copy.
          </p>
          <div className={row}>
            <a className={button({ tone: "primary" })} href={subscribeDeepLink(bundleUrl, code)}>
              Open in the app
            </a>
            <CopyButton value={shareLink(bundleUrl, code)} label="Copy link" />
          </div>
          <p className={muted}>
            On a desktop? Copy the link and open it on the phone that has the app.
          </p>
        </article>
      ) : state.kind === "needsCode" ? (
        <section className={card}>
          <div className={stack}>
            <h1 className={subheading}>This collection needs an access code</h1>
            <p className={muted}>
              The author shares it with the link. Paste the code and nothing about it leaves this page except
              the request for this collection.
            </p>
            <form
              className={row}
              onSubmit={(event) => {
                event.preventDefault();
                const trimmed = typedCode.trim();
                if (trimmed.length > 0) setCode(trimmed);
              }}
            >
              <input
                className={`${field} ${mono}`}
                value={typedCode}
                onChange={(event) => setTypedCode(event.target.value)}
                aria-label="Access code"
                autoComplete="off"
                spellCheck={false}
              />
              <button type="submit" className={button({ tone: "primary" })}>
                Open
              </button>
            </form>
          </div>
        </section>
      ) : state.kind === "gone" ? (
        <section className={stack}>
          <h1 className={heading}>The author deleted this collection</h1>
          <p className={muted}>
            Templates already on a phone stay there. Nothing new will arrive from this link.
          </p>
        </section>
      ) : state.kind === "missing" ? (
        <section className={stack}>
          <h1 className={heading}>Nothing here</h1>
          <p className={muted}>This link does not point at a published collection.</p>
        </section>
      ) : (
        <section className={stack}>
          <p className={errorText}>{state.message}</p>
          <button type="button" className={button()} onClick={() => void load(code)}>
            Try again
          </button>
        </section>
      )}
    </main>
  );
}
